import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import Label from "@dt/Label";
import HelperText from "@dt/HelperText";
import VisuallyHidden from "@dt/VisuallyHidden";
import styles from "./Checkbox.module.css";

export interface CheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "checked" | "size"
  > {
  /** Label text displayed next to the checkbox */
  label?: string;
  /** Whether to show the label text. @default true */
  showLabel?: boolean;
  /** Checked state (controlled). */
  checked?: boolean;
  /** Indeterminate (mixed) state. @default false */
  indeterminate?: boolean;
  /** Disables interaction and dims the control. @default false */
  disabled?: boolean;
  /** Initial checked state for uncontrolled use. */
  defaultChecked?: boolean;
  /** Size. @default "md" */
  size?: "sm" | "md" | "lg";
  /** Called with the next checked value when toggled. */
  onCheckedChange?: (checked: boolean) => void;
  /** Custom ID for the checkbox element; auto-generated when omitted */
  id?: string;
  /** Error message under the control; sets aria-invalid and aria-describedby. */
  error?: string;
  /** Supporting text under the control; always rendered, below `error` when both are set. */
  helperText?: string;
}

/** Accessible checkbox with label, sizes, and indeterminate state. */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      showLabel = true,
      checked,
      indeterminate,
      disabled = false,
      defaultChecked,
      size = "md",
      onCheckedChange,
      error,
      helperText,
      ...props
    },
    ref,
  ) => {
    // Uncontrolled state management
    const [internalChecked, setInternalChecked] = useState(
      defaultChecked ?? false,
    );
    const isControlled = checked !== undefined;
    const actualChecked = isControlled ? checked : internalChecked;
    // Stable fallback id so multiple unlabeled-id Checkbox siblings (e.g. in
    // FormField group mode) don't collide on a shared literal id, which would
    // break label association for all but the first instance.
    const generatedId = useId();
    const id = props.id ?? generatedId;
    // Local ref so the component can reach the input element even when the
    // parent does not forward a ref.
    const innerRef = useRef<HTMLInputElement | null>(null);

    // Merge the forwarded ref with the local ref.
    const setRefs = (el: HTMLInputElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };

    useEffect(() => {
      const element = innerRef.current;
      if (!element) return;

      // Set indeterminate property (native property, not an attribute)
      element.indeterminate = !!indeterminate;

      // Update visual state classes - CSS handles all styling
      if (indeterminate) {
        element.classList.remove(styles.checkedState);
        element.classList.add(styles.indeterminateState);
      } else if (actualChecked) {
        element.classList.remove(styles.indeterminateState);
        element.classList.add(styles.checkedState);
      } else {
        element.classList.remove(styles.indeterminateState);
        element.classList.remove(styles.checkedState);
      }
    }, [actualChecked, indeterminate, props.id]);

    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const describedBy =
      [
        error ? errorId : null,
        helperText ? helperId : null,
        props["aria-describedby"],
      ]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <>
      <div className={styles["checkboxContainer"]}>
        <input
          type="checkbox"
          className={`${styles.checkbox} ${styles[`checkbox--${size}`]} ${actualChecked ? styles.checkedState : ""}`}
          ref={setRefs}
          checked={actualChecked}
          onChange={(e) => {
            // Activating a mixed checkbox always resolves to checked, whatever
            // the native toggle of the underlying value produced.
            const nextChecked = indeterminate ? true : e.target.checked;

            // Update the visual state classes - CSS handles all styling
            const element = innerRef.current;
            if (element) {
              // Clear indeterminate state when user clicks
              element.indeterminate = false;
              element.classList.remove(styles.indeterminateState);

              if (nextChecked) {
                element.classList.add(styles.checkedState);
              } else {
                element.classList.remove(styles.checkedState);
              }
            }

            // Update internal state for uncontrolled mode
            if (!isControlled) {
              setInternalChecked(nextChecked);
            }

            onCheckedChange?.(nextChecked);
          }}
          disabled={disabled}
          {...props}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : props["aria-invalid"]}
        />
        <Label
          htmlFor={id}
          disabled={disabled}
          variant="option"
          className={styles.label}
        >
          {/* Hidden, not removed: the label text stays the accessible name. */}
          {showLabel ? label : label && <VisuallyHidden>{label}</VisuallyHidden>}
        </Label>
      </div>
      {error && (
        <HelperText id={errorId} state="error">
          {error}
        </HelperText>
      )}
      {helperText && (
        <HelperText id={helperId}>{helperText}</HelperText>
      )}
      </>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
